import { Mesh, Object3D, Quaternion, Vector3 } from "three";
import { isCampfireBatchOwned } from "@runtime/assets/entityMeshBatcher";

/**
 * `orientedBounds.ts` — port of Unity's `OrientedBounds.OBB`
 * (`Models/Utils/OBBUtils.cs`) + the vertex gathering in
 * `BoundingBoxViewModel.AccurateBounds`: an object-axis-aligned bounding
 * box grown vertex-by-vertex with the classic half-grow / center-shift
 * accumulate.
 *
 * Web shape: axes are the target node's WORLD basis vectors and extents are
 * WORLD half-sizes along them (Unity round-trips through attached-local
 * units and re-applies lossyScale on draw — algebraically identical for the
 * world-space corners the renderer needs).
 */

export interface OrientedBox {
  readonly center: Vector3;
  readonly axis1: Vector3;
  readonly axis2: Vector3;
  readonly axis3: Vector3;
  /** World half-sizes along axis1 / axis2 / axis3. */
  readonly extents: Vector3;
  /**
   * The lowest vertex of the walked meshes, in WORLD space — the anchor for
   * the height-above-ground ruler and the source of
   * `SnapshotMeasurement.heightAboveGround`.
   *
   * EXACT rather than approximate: a triangle mesh's surface attains its
   * minimum world-Y at a vertex, so scanning vertices IS scanning the surface.
   * A box corner is not — the box encloses every vertex, so on a rotated part
   * its lowest corner sits BELOW the lowest point of the part and a ruler
   * anchored there starts in mid-air under it.
   *
   * Tracked in the walk that was already running and cached by the same
   * transform guard (`BoundingBoxToolController.updateObbCache`), so it costs
   * one comparison per vertex and nothing per frame. It also inherits this
   * walk's two correctness rules for free — batch-owned subtrees skipped,
   * invisible meshes skipped — which is the reason it lives here rather than
   * in a second walk of its own.
   */
  readonly lowestPoint: Vector3;
  /**
   * The highest vertex of the walked meshes, in WORLD space. With
   * {@link lowestPoint} it bounds the part's vertical extent, whose midpoint is
   * the horizontal centreline `SnapshotMeasurement.centerlineHeightAboveGround`
   * reports. Exact for the same reason, and tracked in the same walk.
   */
  readonly highestPoint: Vector3;
}

const scratchPoint = new Vector3();
const scratchDelta = new Vector3();

export function computeOrientedBounds(root: Object3D): OrientedBox | null {
  root.updateWorldMatrix(true, true);
  const quaternion = root.getWorldQuaternion(new Quaternion());
  const axis1 = new Vector3(1, 0, 0).applyQuaternion(quaternion);
  const axis2 = new Vector3(0, 1, 0).applyQuaternion(quaternion);
  const axis3 = new Vector3(0, 0, 1).applyQuaternion(quaternion);

  let center: Vector3 | null = null;
  let lowestPoint: Vector3 | null = null;
  let highestPoint: Vector3 | null = null;
  const extents = new Vector3(0, 0, 0);

  // BATCH-OWNED NODES ARE SKIPPED, AND THAT IS LOAD-BEARING. `EntityMeshBatcher`
  // parents its container under the ROOT SNAPSHOT'S node, so a `BatchedMesh`
  // sits inside the very subtree this walks. That mesh's geometry is one merged
  // buffer holding every batched part's vertices in THEIR OWN LOCAL space - the
  // per-instance placement lives in the batch's matrix texture, applied by the
  // shader and invisible to a vertex walk. Reading it here pins a phantom copy
  // of the whole model at the batch root: invisible when the model straddles
  // that root, and catastrophic when it does not, which is the normal case for
  // a CAD assembly whose origin sits away from its geometry (the reason "Align
  // to CAD Origins" exists). Measured against the real batcher: 1.9x too wide at
  // a 2 m offset, 23.7x at 50 m, 455x at 1000 m, with the centre dragged back
  // toward the root - so the box is both the wrong size and in the wrong place,
  // and the figure quoted beside it is wrong by the same factor.
  //
  // The SOURCE meshes are still walked, which is what keeps this correct:
  // batching moves them to a camera-excluded layer and deliberately leaves
  // `visible` alone, precisely so bounds, transforms and tools keep reading the
  // original hierarchy (`EntityMeshBatcher`'s own class comment).
  //
  // Pruning the SUBTREE rather than testing each node mirrors
  // `explosionTransforms.ts`'s bounds walk, which already got this right; it
  // also survives a future batch child that forgets the marker.
  const stack: Object3D[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (isCampfireBatchOwned(node)) continue;
    for (const child of node.children) stack.push(child);

    const mesh = node as Mesh;
    if (!mesh.isMesh || !mesh.visible) continue;
    const position = mesh.geometry?.getAttribute("position");
    if (!position) continue;
    for (let i = 0; i < position.count; i++) {
      scratchPoint.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      // BEFORE the seed branch below, which `continue`s past the enclose step:
      // put this after it and the very first vertex is never considered, so a
      // single-vertex mesh has no lowest point and a model whose lowest vertex
      // happens to be walked first reports its second-lowest.
      if (!lowestPoint || scratchPoint.y < lowestPoint.y) lowestPoint = scratchPoint.clone();
      if (!highestPoint || scratchPoint.y > highestPoint.y) highestPoint = scratchPoint.clone();
      if (!center) {
        center = scratchPoint.clone();
        continue;
      }
      encloseWorldPoint(center, extents, axis1, axis2, axis3, scratchPoint);
    }
  }

  if (!center || !lowestPoint || !highestPoint) return null;
  return { center, axis1, axis2, axis3, extents, lowestPoint, highestPoint };
}

/** Unity `OrientedBounds.Enclose` — grow half the overshoot, shift center. */
function encloseWorldPoint(
  center: Vector3,
  extents: Vector3,
  axis1: Vector3,
  axis2: Vector3,
  axis3: Vector3,
  point: Vector3,
): void {
  scratchDelta.copy(point).sub(center);
  let shift1 = 0;
  let shift2 = 0;
  let shift3 = 0;

  const e1 = scratchDelta.dot(axis1);
  if (Math.abs(e1) > extents.x) {
    shift1 = 0.5 * (e1 + (e1 < 0 ? extents.x : -extents.x));
    extents.x = 0.5 * (extents.x + Math.abs(e1));
  }
  const e2 = scratchDelta.dot(axis2);
  if (Math.abs(e2) > extents.y) {
    shift2 = 0.5 * (e2 + (e2 < 0 ? extents.y : -extents.y));
    extents.y = 0.5 * (extents.y + Math.abs(e2));
  }
  const e3 = scratchDelta.dot(axis3);
  if (Math.abs(e3) > extents.z) {
    shift3 = 0.5 * (e3 + (e3 < 0 ? extents.z : -extents.z));
    extents.z = 0.5 * (extents.z + Math.abs(e3));
  }

  if (shift1 !== 0 || shift2 !== 0 || shift3 !== 0) {
    center.addScaledVector(axis1, shift1);
    center.addScaledVector(axis2, shift2);
    center.addScaledVector(axis3, shift3);
  }
}
